import React, { useState, useEffect } from "react";
import i18n from "i18next";

import { ILuebOption } from "../../Models/ILuebOption";

import { MmInch } from "../../helpers/mminch";
import { helperMethods } from "../../helpers/helperMethods";
import { fetchWrapper } from "../../helpers/fetchwrapper";

import { baseUrl } from "../../helpers/constants";
import useWindowSize from "../../helpers/useWindowSize";
import {
  Dropdown,
  Field,
  Image,
  ImageSwatch,
  Input,
  Label,
  Option,
  Subtitle1,
  SwatchPicker,
} from "@fluentui/react-components";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import { DismissRegular } from "@fluentui/react-icons";
import MeasurementInputs, { IMeasurements } from "./MeasurementInputs";

interface IAngleHeadSpecificationsProps {
  configuration: any;
  antriebsProfil: any;
  schraubfall: string;
  onChangeAngleHeadSpecifications: any;
}

const dropdownStyles = { dropdown: { maxWidth: "200px" } };

function AngleHeadSpecifications(props: IAngleHeadSpecificationsProps) {
  const [angleHeadOutputOptions, setAngleHeadOutputOptions] = useState<
    ILuebOption[]
  >([]);

  const [imagesPreload, setImagesPreload] = useState<any>([]);
  const [currentFocus, setCurrentFocus] = useState("");
  const [specification, setSpecification] = useState({
    durchmesserDMax: { mm: 0.0, inchDez: 0.0, inch: "" },
    heigthHMax: { mm: 0.0, inchDez: 0.0, inch: "" },
    shaftAngle: "all",
    winkelKopfAbtriebsProfil: { key: "", kurzwort: "", text: "" },
    output: { key: "", text: "" },
    nussDurchmesserDMax: { mm: 0.0, inchDez: 0.0, inch: "" },
    nussLaengeLmax: { mm: 0.0, inchDez: 0.0, inch: "" },
    nussLaengeLmin: { mm: 0.0, inchDez: 0.0, inch: "" },
    nutAbtriebsProfil: { key: "", kurzwort: "", text: "" },
  });

  useEffect(() => {
    if (props.configuration) {
      setSpecification(props.configuration);
    }
  }, []);

  const [winkelKopfAbtriebsProfile, setWinkelKopfAbtriebsProfile] = useState<
    ILuebOption[]
  >([]);
  const [nutAbtriebsProfile, setNutAbtriebsProfile] = useState<ILuebOption[]>(
    [],
  );

  const [imageName, setImageName] = useState("");

  const handleSpecificationValuesChange = (name: string, val: any) => {
    setSpecification((prevState) => ({
      ...prevState,
      [name]: val,
    }));
  };

  useEffect(() => {
    props.onChangeAngleHeadSpecifications(specification);
  }, [specification]);

  const shaftAngleOptions: ILuebOption[] = [
    { key: "all", text: i18n.t<string>("all") }, // -> 0
    { key: "90", text: "90°" },
    { key: "110", text: "110°" },
  ];

  const bitholderEnabled =
    props.schraubfall == "schraube" &&
    props.antriebsProfil.data.isMaleProfil == false;
  const nutEnabled =
    props.schraubfall == "mutter" ||
    (props.schraubfall == "schraube" &&
      props.antriebsProfil.data.isMaleProfil == true);
  const flushSocketEnabled =
    props.schraubfall == "schraube" &&
    props.antriebsProfil.data.isMaleProfil == true;
  const holderEnabled = props.schraubfall == "holdndrive";

  useEffect(() => {
    const options: ILuebOption[] = [
      {
        key: "bitholder",
        disabled: !bitholderEnabled,
        icon: `${baseUrl}/IMAGES/WINKELKOPF/WK_BIT_200X200PX.PNG`,
        text: i18n.t<string>("angleHeadWithBitholder"), // This text is long to show text wrapping.
      },
      {
        key: "nut",
        disabled: !nutEnabled,
        icon: `${baseUrl}/IMAGES/WINKELKOPF/WK_NUSS_200X200PX.PNG`,
        text: i18n.t<string>("angleHeadWithNut"), // This text is long to show text wrapping.
      },
      {
        key: "flushsocket",
        disabled: !flushSocketEnabled,
        icon: `${baseUrl}/IMAGES/WINKELKOPF/WK_FLUSHSOCKET_200X200PX.PNG`,
        text: i18n.t<string>("angleHeadWithFlushSocket"), // This text is long to show text wrapping.
      },
      {
        key: "holder",
        disabled: !holderEnabled,
        icon: `${baseUrl}/IMAGES/WINKELKOPF/WK_GEGENHALTER_200X200PX.PNG`,
        text: i18n.t<string>("angleHeadWithHolder"), // This text is long to show text wrapping.
      },
    ];

    setAngleHeadOutputOptions(options);

    const firstOption = options.find((x) => x.disabled == false);

    handleSpecificationValuesChange("output", {
      key: firstOption?.key,
      text: firstOption?.text,
    });
  }, [props.schraubfall, props.antriebsProfil]);

  const getValFromArray = (array: any, field: string) => {
    let test = undefined;
    for (var i = 0, iLen = Object.entries(array).length; i < iLen; i++) {
      // do stuff with node.childNodes[i];
      if (Object.entries(array)[i][0] == field) {
        test = Object.entries(array)[i][1];
      }
    }
    return test;
  };

  const inchConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const inchDezValue = getValFromArray(fieldValue, "inchDez");
    const asNumber = helperMethods.convertToNumber(inchDezValue);
    const mm = helperMethods.convertToNumber(MmInch.toMM(asNumber));
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: asNumber,
      inch: asNumber == 0 ? "" : MmInch.toInch(mm),
    });
  };

  const fractionalConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const inchValue = getValFromArray(fieldValue, "inch");
    const mm = helperMethods.convertToNumber(MmInch.toMM(inchValue));
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: mm / 25.4,
      inch: inchValue,
    });
  };

  const mmConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const mmValue = getValFromArray(fieldValue, "mm");
    const mm = helperMethods.convertToNumber(mmValue as string);
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: mm / 25.4,
      inch: mm ? MmInch.toInch(mm) : "",
    });
  };

  useEffect(() => {
    getWinkelKopfSchraubprofile(specification.output.key);
    getNutBitProfile();
  }, [specification.output.key]);

  useEffect(() => {}, []);

  const windowSize = useWindowSize();
  const textFieldSize = windowSize.width > 500 ? 170 : "100%";

  const getNutBitProfile = () => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/configurator/GetGegenprofile?profil=${props.antriebsProfil.kurzwort}`,
      )
      .then((data: any) => {
        let options: ILuebOption[] = [];
        data.forEach((element: any) => {
          let option: ILuebOption = {
            data: element,
            key: element.schraubprofilGuid,
            text: i18n.language.includes("de")
              ? element.displayNameDE
              : element.displayNameEN,
          };
          options.push(option);
        });
        setNutAbtriebsProfile(options);

        if (options.length > 0) {
          handleSpecificationValuesChange("nutAbtriebsProfil", {
            key: options[0].key.toString(),
            kurzwort: options[0].data.kurzwort.toString(),
            text: options[0].text.toString(),
          });
        } else {
          handleSpecificationValuesChange("nutAbtriebsProfil", {
            key: "",
            kurzwort: "",
            text: "",
          });
        }
      });
  };

  const getWinkelKopfSchraubprofile = (key: string) => {
    // Wenn Female dann "BH Bithalter", wenn Male dann SD - Außenvierkant, sonst 'HEX', 'IDH', 'SFD', 'DSD' OR 'PF';
    let kurzwort = "";

    if (key == "bitholder") {
      kurzwort = "BH";
    } else if (key == "nut" || key == "flushsocket") {
      kurzwort = "ASD";
    } else if (key == "holder") {
      kurzwort = "ISW;IDH;SFD;DSD;PF";
    }

    if (kurzwort != "") {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/configurator/GetByKurzwort?kurzwort=${kurzwort}`,
        )
        .then((data: any) => {
          let options: ILuebOption[] = [];
          let images: string[] = imagesPreload;
          data.forEach((element: any) => {
            let option: ILuebOption = {
              data: element,
              key: element.schraubprofilGuid,
              text: i18n.language.includes("de")
                ? element.displayNameDE
                : element.displayNameEN,
            };
            options.push(option);

            // Workaround: Bilder cachen
            const imageUrl = `${baseUrl}/IMAGES/PROFILBILDER/SOCKET_${element.kurzwort}.JPG`;
            if (!images.some((x: string) => x == imageUrl)) {
              images.push(imageUrl);
            }
          });

          setImagesPreload(images);

          setWinkelKopfAbtriebsProfile(options);
          handleSpecificationValuesChange("winkelKopfAbtriebsProfil", {
            key: options[0].key.toString(),
            kurzwort: options[0].data.kurzwort.toString(),
            text: options[0].text.toString(),
            data: options[0].data,
          });
        });
    } else {
      setWinkelKopfAbtriebsProfile([]);
      handleSpecificationValuesChange("winkelKopfAbtriebsProfil", {
        key: "",
        kurzwort: "",
        text: "",
      });
    }
  };

  const onChangeWinkelKopfAbtriebsProfil = (ev: any, val: any) => {
    handleSpecificationValuesChange("winkelKopfAbtriebsProfil", {
      key: val.key,
      kurzwort: val.data.kurzwort,
      text: val.text,
      data: val.data,
    });
  };

  const onChangeNutAbtriebsProfil = (ev: any, val: any) => {
    handleSpecificationValuesChange("nutAbtriebsProfil", {
      key: val.key,
      kurzwort: val.data.kurzwort,
      text: val.text,
      data: val.data,
    });
  };

  return (
    <div>
      <Subtitle1 style={{ marginLeft: "10px" }}>
        {i18n.t<string>("angleHeadSpecifications")}
      </Subtitle1>
      <div className="lueb-MarginTop10">
        <div style={{ display: "flex", alignItems: "center" }}>
          <div>
            <MeasurementInputs
              title={"durchmesserDmax"}
              tabIndex={1}
              currentMeasurements={specification.durchmesserDMax}
              onChangeMeasurements={(measurements: IMeasurements) => {
                setCurrentFocus("");
                handleSpecificationValuesChange(
                  "durchmesserDMax",
                  measurements,
                );
              }}
              onFocus={() => {
                setCurrentFocus("D");
              }}
            />

            <MeasurementInputs
              title={"heigthHmax"}
              tabIndex={2}
              currentMeasurements={specification.heigthHMax}
              onChangeMeasurements={(measurements: IMeasurements) => {
                setCurrentFocus("");
                handleSpecificationValuesChange("heigthHMax", measurements);
              }}
              onFocus={() => {
                setCurrentFocus("H");
              }}
            />

            <Label style={{ marginTop: "8px" }}>
              {i18n.t<string>("shaftAngle")}
            </Label>
            <StackShim horizontal tokens={{ childrenGap: 5 }} wrap={true}>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Field>
                  <Dropdown
                    tabIndex={3}
                    onFocus={() => {
                      setCurrentFocus("AW");
                    }}
                    selectedOptions={[specification.shaftAngle]}
                    onBlur={() => {
                      setCurrentFocus("");
                    }}
                    onOptionSelect={(ev, val) => {
                      handleSpecificationValuesChange(
                        "shaftAngle",
                        val.optionValue,
                      );
                    }}
                    value={
                      shaftAngleOptions.find(
                        (x) => x.key == specification.shaftAngle,
                      )?.text
                    }
                  >
                    {shaftAngleOptions.map((item) => {
                      if (typeof item.key == "string") {
                        return <Option value={item.key}>{item.text}</Option>;
                      }
                    })}
                  </Dropdown>
                </Field>
              </StackItemShim>
            </StackShim>
          </div>
          {currentFocus != "" && (
            <Image
              style={{
                marginLeft: "10px",
                border: currentFocus ? "2px #ff6600 solid" : "",
                borderRadius: currentFocus ? "20px" : "",
              }}
              height="120px"
              src={`${baseUrl}/IMAGES/WINKELKOPF/MASS_WK_${currentFocus}_200X200.PNG`}
            />
          )}
          {currentFocus == "" && (
            <Image
              style={{
                marginLeft: "10px",
                border: currentFocus ? "" : "2px white solid",
              }}
              height="120px"
              src={`${baseUrl}/IMAGES/WINKELKOPF/MASS_WK_L_200X200.PNG`}
            />
          )}
        </div>
        <Label style={{ marginTop: "8px" }}>
          {i18n.t<string>("abtriebsprofil")}
        </Label>
        <div style={{ width: "35vw" }}>
          <StackShim horizontal tokens={{ childrenGap: 10 }} wrap={true}>
            <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
              <Field style={{ maxWidth: "80%" }}>
                <Dropdown
                  tabIndex={4}
                  selectedOptions={[specification.winkelKopfAbtriebsProfil.key]}
                  value={specification.winkelKopfAbtriebsProfil.text}
                  onOptionSelect={(ev, data) => {
                    var abtriebsProfil: ILuebOption | undefined;
                    if (
                      data.optionValue != undefined &&
                      winkelKopfAbtriebsProfile
                    ) {
                      abtriebsProfil = winkelKopfAbtriebsProfile.find(
                        (x) => x.key == data.optionValue,
                      );
                      if (abtriebsProfil != undefined) {
                        handleSpecificationValuesChange(
                          "winkelKopfAbtriebsProfil",
                          {
                            key: abtriebsProfil.key,
                            kurzwort: abtriebsProfil.data.kurzwort,
                            text: abtriebsProfil.text,
                            data: abtriebsProfil.data,
                          },
                        );
                      }
                    }
                  }}
                >
                  {winkelKopfAbtriebsProfile.map((item) => {
                    if (typeof item.key == "string") {
                      return <Option value={item.key}>{item.text}</Option>;
                    }
                  })}
                </Dropdown>
              </Field>
            </StackItemShim>
            <StackItemShim grow={1} style={{ paddingLeft: 5, paddingRight: 5 }}>
              <Field>
                {specification.winkelKopfAbtriebsProfil.kurzwort && (
                  <Image
                    width="80px"
                    height="80px"
                    src={`${baseUrl}/IMAGES/PROFILBILDER/SOCKET_${specification.winkelKopfAbtriebsProfil.kurzwort}.JPG`}
                  />
                )}
              </Field>
            </StackItemShim>
          </StackShim>
        </div>
        <div>
          <h6>{i18n.t<string>("selectOutput")}</h6>
          <SwatchPicker
            selectedValue={specification.output.key}
            onSelectionChange={(ev, val) => {
              var selectedImage = angleHeadOutputOptions.find(
                (x) => x.key == val.selectedValue,
              );
              handleSpecificationValuesChange("output", {
                key: selectedImage?.key,
                text: selectedImage?.text,
              });
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {angleHeadOutputOptions.map((item) => {
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "10px",
                    }}
                  >
                    <ImageSwatch
                      style={{
                        width: 95,
                        height: 95,
                        filter: item.disabled == true ? "grayscale(1)" : "",
                      }}
                      disabled={item.disabled}
                      value={typeof item.key == "string" ? item.key : ""}
                      src={item.icon ? item.icon : ""}
                    />

                    <Label style={{ marginTop: "5px" }} weight="semibold">
                      {item.text}
                    </Label>
                  </div>
                );
              })}
            </div>
          </SwatchPicker>
        </div>
        {(specification.output.key == "nut" ||
          specification.output.key == "flushsocket" ||
          specification.output.key == "bitholder") && (
          <>
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <div>
                <MeasurementInputs
                  title={
                    specification.output.key == "nut"
                      ? "nutSpecifications"
                      : specification.output.key == "flushsocket"
                      ? "flushSocketSpecifications"
                      : specification.output.key == "bitholder"
                      ? "bitholderSpecifications"
                      : ""
                  }
                  tabIndex={5}
                  currentMeasurements={specification.nussDurchmesserDMax}
                  onChangeMeasurements={(measurements: IMeasurements) => {
                    setImageName("");
                    handleSpecificationValuesChange(
                      "nussDurchmesserDMax",
                      measurements,
                    );
                  }}
                  onFocus={() => {
                    setImageName("MASS_NUSS_D");
                  }}
                />

                <MeasurementInputs
                  title={"laengeLmax"}
                  tabIndex={6}
                  currentMeasurements={specification.nussLaengeLmax}
                  onChangeMeasurements={(measurements: IMeasurements) => {
                    setCurrentFocus("");
                    handleSpecificationValuesChange(
                      "nussLaengeLmax",
                      measurements,
                    );
                  }}
                  onFocus={() => {
                    setCurrentFocus("MASS_NUSS_L");
                  }}
                />

                <MeasurementInputs
                  title={"laengeLmin"}
                  tabIndex={7}
                  currentMeasurements={specification.nussLaengeLmin}
                  onChangeMeasurements={(measurements: IMeasurements) => {
                    setCurrentFocus("");
                    handleSpecificationValuesChange(
                      "nussLaengeLmin",
                      measurements,
                    );
                  }}
                  onFocus={() => {
                    setCurrentFocus("MASS_NUSS_L");
                  }}
                />
                <Label style={{ marginTop: "8px" }}>
                  {i18n.t<string>("abtriebsprofil")}
                </Label>
                <div style={{ width: "35vw" }}>
                  <StackShim
                    horizontal
                    tokens={{ childrenGap: 10 }}
                    wrap={true}
                  >
                    <StackItemShim
                      grow={1}
                      style={{ paddingLeft: 5, paddingRight: 5 }}
                    >
                      <Field style={{ maxWidth: "80%" }}>
                        <Dropdown
                          tabIndex={9}
                          selectedOptions={[
                            specification.nutAbtriebsProfil.key,
                          ]}
                          value={specification.nutAbtriebsProfil.text}
                          onOptionSelect={(ev, data) => {
                            var abtriebsProfil: ILuebOption | undefined;
                            if (
                              data.optionValue != undefined &&
                              nutAbtriebsProfile
                            ) {
                              abtriebsProfil = nutAbtriebsProfile.find(
                                (x) => x.key == data.optionValue,
                              );
                              if (abtriebsProfil != undefined) {
                                handleSpecificationValuesChange(
                                  "nutAbtriebsProfil",
                                  {
                                    key: abtriebsProfil.key,
                                    kurzwort: abtriebsProfil.data.kurzwort,
                                    text: abtriebsProfil.text,
                                    data: abtriebsProfil.data,
                                  },
                                );
                              }
                            }
                          }}
                        >
                          {nutAbtriebsProfile.map((item) => {
                            if (typeof item.key == "string") {
                              return (
                                <Option value={item.key}>{item.text}</Option>
                              );
                            }
                          })}
                        </Dropdown>
                      </Field>
                    </StackItemShim>
                    <StackItemShim
                      grow={1}
                      style={{ paddingLeft: 5, paddingRight: 5 }}
                    >
                      <Field>
                        {specification.nutAbtriebsProfil.kurzwort && (
                          <Image
                            className="lueb-MarginTop10"
                            width="80px"
                            src={`${baseUrl}/IMAGES/PROFILBILDER/SOCKET_${specification.nutAbtriebsProfil.kurzwort}.JPG`}
                          />
                        )}
                      </Field>
                    </StackItemShim>
                  </StackShim>
                </div>
              </div>
              <div style={{ width: 300, marginLeft: 10 }}>
                {imageName != "" && (
                  <Image
                    width={300}
                    src={`${baseUrl}/IMAGES/CONFIST/MASS/${imageName}.PNG`}
                  />
                )}
                {imageName == "" && (
                  <div
                    style={{
                      background: "white",
                      width: 364,
                      height: 300,
                    }}
                  ></div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AngleHeadSpecifications;
